const PLUGIN_NODE_V2_TYPE = "PluginNodeV2";
const DEFAULT_PLUGIN_NODE_TITLE = "Plugin Node";

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

export function isPluginNodeType(type: string | undefined): boolean {
  return type === PLUGIN_NODE_V2_TYPE;
}

export function getConfiguredPluginNodeDisplayName(
  nodeType: string | undefined,
  nodeConfig: Record<string, unknown> | undefined
): string | undefined {
  if (!isPluginNodeType(nodeType)) return undefined;
  return nonEmptyString(nodeConfig?.plugin_display_name);
}

function isGenericPluginName(
  name: string | undefined,
  registryTitle: string | undefined
): boolean {
  if (!name) return true;
  return name === DEFAULT_PLUGIN_NODE_TITLE || name === registryTitle;
}

/**
 * Resolve the identity of the generic PluginNodeV2 shell without overwriting a
 * user rename. Dynamic config wins over the picker-seeded title while the
 * node still has its generic registry name.
 */
export function resolvePluginNodeDisplayName({
  nodeType,
  nodeConfig,
  currentName,
  registryTitle,
  dynamicTitle,
}: {
  nodeType: string | undefined;
  nodeConfig: Record<string, unknown> | undefined;
  currentName: string | null | undefined;
  registryTitle?: string | null;
  dynamicTitle?: string | null;
}): string | undefined {
  const current = nonEmptyString(currentName);
  if (!isPluginNodeType(nodeType)) return current;

  const registry = nonEmptyString(registryTitle);
  if (!isGenericPluginName(current, registry)) return current;

  return (
    nonEmptyString(dynamicTitle) ??
    getConfiguredPluginNodeDisplayName(nodeType, nodeConfig) ??
    current ??
    registry
  );
}

/** Show a loading identity only for an unresolved generic plugin shell. */
export function shouldShowPluginNodeIdentityLoading({
  nodeType,
  nodeConfig,
  currentName,
  registryTitle,
  dynamicTitle,
  isConfigLoading,
}: {
  nodeType: string | undefined;
  nodeConfig: Record<string, unknown> | undefined;
  currentName: string | null | undefined;
  registryTitle?: string | null;
  dynamicTitle?: string | null;
  isConfigLoading: boolean;
}): boolean {
  if (!isPluginNodeType(nodeType) || !isConfigLoading) return false;

  const current = nonEmptyString(currentName);
  const registry = nonEmptyString(registryTitle);
  return (
    isGenericPluginName(current, registry) &&
    !nonEmptyString(dynamicTitle) &&
    !getConfiguredPluginNodeDisplayName(nodeType, nodeConfig)
  );
}
